/**
 * StudySync Study Service
 * Handles all study-related API operations
 */

import { api, authenticatedApi } from '../utils/api';
import { API_ENDPOINTS } from '../config/api';
import type {
  Study,
  CreateStudyRequest,
  StudyStats,
  StudyCodeVerificationResponse,
  ApiResponse,
} from '../types';

export class StudyService {
  /**
   * Verify a study code
   */
  static async verifyStudyCode(studyCode: string): Promise<StudyCodeVerificationResponse> {
    return api.post<StudyCodeVerificationResponse>(
      API_ENDPOINTS.AUTH.VERIFY_STUDY_CODE,
      { studyCode }
    );
  }

  /**
   * Create a new study
   */
  static async createStudy(
    studyData: CreateStudyRequest,
    token: string
  ): Promise<ApiResponse<{ studyId: string; studyCode: string }>> {
    return authenticatedApi.post(
      API_ENDPOINTS.AUTH.CREATE_STUDY,
      studyData,
      token
    );
  }

  /**
   * Get all studies for authenticated researcher
   */
  static async getResearcherStudies(token: string): Promise<ApiResponse<Study[]>> {
    return authenticatedApi.get<ApiResponse<Study[]>>(
      API_ENDPOINTS.AUTH.RESEARCHER_STUDIES,
      token
    );
  }

  /**
   * Get a specific study by ID
   */
  static async getStudy(studyId: string, token: string): Promise<ApiResponse<Study>> {
    return authenticatedApi.get<ApiResponse<Study>>(
      API_ENDPOINTS.STUDIES.GET(studyId),
      token
    );
  }

  /**
   * Update a study
   */
  static async updateStudy(
    studyId: string,
    updates: Partial<Study>,
    token: string
  ): Promise<ApiResponse> {
    return authenticatedApi.put(
      API_ENDPOINTS.AUTH.UPDATE_STUDY(studyId),
      updates,
      token
    );
  }

  /**
   * Delete a study
   */
  static async deleteStudy(studyId: string, token: string): Promise<ApiResponse> {
    return authenticatedApi.delete(
      API_ENDPOINTS.AUTH.DELETE_STUDY(studyId),
      token
    );
  }

  /**
   * Get study statistics
   */
  static async getStudyStats(studyId: string, token: string): Promise<ApiResponse<StudyStats>> {
    return authenticatedApi.get<ApiResponse<StudyStats>>(
      API_ENDPOINTS.STUDIES.STATS(studyId),
      token
    );
  }

  /**
   * Get participants for a study
   */
  static async getStudyParticipants(studyId: string, token: string): Promise<ApiResponse> {
    return authenticatedApi.get(
      API_ENDPOINTS.STUDIES.PARTICIPANTS(studyId),
      token
    );
  }

  /**
   * Get study members (main researcher + collaborators)
   */
  static async getStudyMembers(studyId: string, token: string): Promise<ApiResponse> {
    return authenticatedApi.get(
      API_ENDPOINTS.STUDIES.MEMBERS(studyId),
      token
    );
  }

  /**
   * Get pending participants for a specific study
   */
  static async getPendingParticipants(studyId: string, token: string): Promise<ApiResponse> {
    return authenticatedApi.get(
      `/api/studies/${studyId}/pending-participants`,
      token
    );
  }

  /**
   * Add a pending participant to a study
   */
  static async addPendingParticipant(
    studyId: string,
    participantData: {
      email: string;
      firstName?: string;
      lastName?: string;
      phoneNumber?: string;
    },
    token: string
  ): Promise<ApiResponse> {
    return authenticatedApi.post(
      `/api/studies/${studyId}/pending-participants`,
      participantData,
      token
    );
  }

  /**
   * Update a pending participant
   */
  static async updatePendingParticipant(
    studyId: string,
    participantId: string,
    updateData: {
      firstName?: string;
      lastName?: string;
      phoneNumber?: string;
    },
    token: string
  ): Promise<ApiResponse> {
    return authenticatedApi.put(
      `/api/studies/${studyId}/pending-participants/${participantId}`,
      updateData,
      token
    );
  }

  /**
   * Remove a pending participant from a study
   */
  static async removePendingParticipant(
    studyId: string,
    participantId: string,
    token: string,
    device?: 'fitbit' | 'ultrahuman'
  ): Promise<ApiResponse> {
    // With a device, a person who also has another device keeps their record: only that
    // device's waiting invitation is cancelled.
    return authenticatedApi.delete(
      `/api/studies/${studyId}/pending-participants/${participantId}${device ? `?device=${device}` : ''}`,
      token
    );
  }
}

export default StudyService;
